import {
  useCallback, useMemo, useRef, useState, type KeyboardEvent
} from "react";

/**
 * State machine for the multi-box one-time-code input shared by the SMS and
 * iMessage pairing modals: per-digit values, focus refs, paste handling,
 * advance-on-entry, backspace-to-previous, and auto-submit when full.
 *
 * ``onComplete`` is invoked with the full code once every box is filled (or a
 * full code is pasted). It is read fresh on each keystroke (via a ref), so
 * callers can pass a non-memoized handler without stale-closure concerns.
 *
 * Every returned member has a stable identity across renders, and the returned
 * object only changes identity when ``digits`` changes. Consumers can therefore
 * list ``otp.focusFirst`` / ``otp.reset`` in effect dependency arrays and the
 * effect won't re-fire on unrelated re-renders (a keystroke or a countdown
 * tick) — depending on the whole ``otp`` object instead would steal focus on
 * every render and break manual digit entry.
 */
export function useOtpInput(length: number, onComplete: (code: string) => void) {
  const [digits, setDigits] = useState<string[]>(() => Array(length).fill(""));
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Mirror the latest onComplete + digits into refs so the callbacks below can
  // keep stable identities (memoized once) while still reading current values:
  //  - onComplete: callers pass a fresh closure each render; read it live.
  //  - digits: avoids the change/keydown handlers closing over the state value,
  //    which would force them (and the memoized return) to re-create per render.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const digitsRef = useRef(digits);
  digitsRef.current = digits;

  const reset = useCallback(() => setDigits(Array(length).fill("")), [length]);
  const focusFirst = useCallback(() => refs.current[0]?.focus(), []);

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (value.length > 1) {
        // Pasted a full code
        const pasted = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, length).split("");
        const newOtp = Array(length).fill("");
        pasted.forEach((d, i) => {
          newOtp[i] = d;
        });
        setDigits(newOtp);
        const nextIndex = Math.min(pasted.length, length - 1);
        refs.current[nextIndex]?.focus();
        if (pasted.length === length) {
          onCompleteRef.current(newOtp.join(""));
        }
        return;
      }

      const digit = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
      const newOtp = [...digitsRef.current];
      newOtp[index] = digit;
      setDigits(newOtp);

      if (digit && index < length - 1) {
        refs.current[index + 1]?.focus();
      }

      if (digit && newOtp.every((d) => d !== "")) {
        onCompleteRef.current(newOtp.join(""));
      }
    },
    [length]
  );

  const handleKeyDown = useCallback((index: number, e: KeyboardEvent) => {
    if (e.key === "Backspace" && !digitsRef.current[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  }, []);

  return useMemo(
    () => ({
      digits, setDigits, refs, reset, focusFirst, handleChange, handleKeyDown
    }),
    [digits, reset, focusFirst, handleChange, handleKeyDown]
  );
}
